import React, { useState, useMemo } from 'react';
import { Search, ChevronRight, Loader2 } from 'lucide-react';
import styles from './ClientList.module.scss';
import { EmptyRow } from '../EmptyRow/EmptyRow';
import { money } from '../../utils';
import type { Client } from '../../types';

interface ClientListProps {
  clients: Client[];
  loading: boolean;
  onSelect: (client: Client) => void;
}

export function ClientList({ clients, loading, onSelect }: ClientListProps) {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    if (!query.trim()) return clients;
    const q = query.toLowerCase();
    return clients.filter(
      (c) => c.name.toLowerCase().includes(q) || c.id.toLowerCase().includes(q)
    );
  }, [clients, query]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Billed clients</h1>
          <p className={styles.subtitle}>
            {clients.length} client{clients.length === 1 ? '' : 's'} on file
          </p>
        </div>
        <div className={styles.searchWrap}>
          <Search size={15} className={styles.searchIcon} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search clients"
            className={styles.searchInput}
          />
        </div>
      </div>

      <div className={styles.panel}>
        <table className={styles.table}>
          <thead>
            <tr className={styles.headRow}>
              {['Client', 'GST No', 'Total Outstanding', ''].map((h, i) => (
                <th key={i} className={styles.headCell}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={4} className={styles.loadingCell}>
                  <div className={styles.loadingRow}>
                    <Loader2 size={15} className={styles.spinner} />
                    Loading clients
                  </div>
                </td>
              </tr>
            )}
            {!loading && filtered.length === 0 && (
              <EmptyRow label="No clients match that search." span={4} />
            )}
            {!loading &&
              filtered.map((c) => (
                <tr
                  key={c.id}
                  onClick={() => onSelect(c)}
                  className={styles.row}
                >
                  <td className={styles.nameCell}>
                    <div className={styles.name}>{c.name}</div>
                    <div className={styles.id}>{c.billingEmail}</div>
                  </td>
                  <td className={styles.text}>{c.gst_no}</td>
                  <td className={styles.amount}>{money(c.outstanding)}</td>
                  <td className={styles.chevronCell}>
                    <ChevronRight size={16} className={styles.chevron} />
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
